import { ethers } from "ethers";
import { Web3Auth, WEB3AUTH_NETWORK } from "@web3auth/modal";
import { publicEnvConfig } from "../config/config";
import { WalletProviderType } from "../services/enum/wallet-provider-type";
import {
  clearWalletData,
  getStoredWalletData,
  storeWalletData,
} from "./walletUtils";
import { MetamaskErrorCodes } from "../services/enum/metamask-error-codes";
import type { Wallet } from "@/app/types/wallet";
export let web3Auth: Web3Auth;
export let web3AuthInitialized: boolean = false;
let web3AuthInitPromise: Promise<void> | null = null;

export function setweb3AuthInitialized(isIntialize: boolean) {
  web3AuthInitialized = isIntialize;
}

const WEB3AUTH_EVM_CHAINS = [
  {
    chainNamespace: "eip155" as const,
    chainId: "0x1",
    rpcTarget:
      publicEnvConfig.infuraProjectId
        ? `https://mainnet.infura.io/v3/${publicEnvConfig.infuraProjectId}`
        : "https://ethereum-rpc.publicnode.com",
    displayName: "Ethereum Mainnet",
    blockExplorerUrl: "https://etherscan.io",
    ticker: "ETH",
    tickerName: "Ethereum",
    logo: "https://etherscan.io/images/brandassets/etherscan-logo-circle.png",
  },
];

const getWeb3AuthNetwork = () => {
  const envNetwork = (publicEnvConfig.webAuthEnv || "").toLowerCase();
  if (envNetwork === "sapphire_mainnet") {
    return WEB3AUTH_NETWORK.SAPPHIRE_MAINNET;
  }
  return WEB3AUTH_NETWORK.SAPPHIRE_DEVNET;
};

const web3 = new Web3Auth({
  clientId: publicEnvConfig.web3ProjectId,
  web3AuthNetwork: getWeb3AuthNetwork(),
  chains: WEB3AUTH_EVM_CHAINS,
  defaultChainId: "0x1",
});

export class Web3Authentication implements Wallet {
  activeProvider: undefined | ethers.providers.Web3Provider;
  private walletMethod: WalletProviderType = WalletProviderType.WEB3AUTH_WEB2;

  async connect() {
    try {
      if (!web3AuthInitialized) {
        await this.initWeb3Auth();
      }
      const web3authProvider =
        web3.connected && web3.provider ? web3.provider : await web3.connect();

      if (!web3authProvider) {
        console.error("Failed to connect to Web3Auth");
        return Promise.reject("Failed to connect to Web3Auth");
      }
      const provider = new ethers.providers.Web3Provider(web3authProvider);

      const userInfo = await web3.getUserInfo();
      this.walletMethod =
        Object.keys(userInfo).length === 0
          ? WalletProviderType.WEB3AUTH_WEB3
          : WalletProviderType.WEB3AUTH_WEB2;

      const signer = provider.getSigner();
      const accounts = await signer.getAddress();

      this.listenToEvents(provider);
      return this.persistLastUsedWallet(WalletProviderType.WEB3AUTH, accounts);
    } catch (err) {
      console.error("Error occurred while connecting to Web3Auth", err);
      const error = this.handleWalletError(
        MetamaskErrorCodes.NO_METAMASK_PLUGIN
      );
      return Promise.reject(error);
    }
  }

  async disconnect(): Promise<void> {
    try {
      if (!web3AuthInitialized) {
        await this.initWeb3Auth();
      }
      await web3.logout();
    } catch (error) {
      console.warn(error);
    }
    clearWalletData();
  }

  async signTransaction(
    walletId: string,
    challengeMessage: string
  ): Promise<string> {
    let signature;
    if (this.activeProvider) {
      try {
        const signer = this.activeProvider.getSigner(walletId);
        signature = await signer.signMessage(challengeMessage);
        return Promise.resolve(signature);
      } catch (e) {
        return Promise.reject("unable to sign Message");
      }
    } else {
      if (!web3AuthInitialized) {
        await this.initWeb3Auth();
      }
      const web3authProvider = web3.connected ? web3.provider : null;
      if (web3authProvider) {
        const provider = new ethers.providers.Web3Provider(web3authProvider);
        const signer = provider.getSigner(walletId);
        signature = await signer.signMessage(challengeMessage);
        return Promise.resolve(signature);
      } else {
        return Promise.reject("unable to sign Message");
      }
    }
    // TODO: improve errorHandling
    // else return Promise.reject("unable to sign Message");
  }

  async isConnected(): Promise<
    string | { accounts: any; providerType: WalletProviderType }
  > {
    if (!web3AuthInitialized) {
      await this.initWeb3Auth();
    }
    const walletProviderType = WalletProviderType.WEB3AUTH;

    if (web3.connected && web3.provider) {
      const provider = new ethers.providers.Web3Provider(web3.provider);
      const acc = await provider.listAccounts();
      if (!acc[0]) {
        return "";
      }

      const userInfo = await web3.getUserInfo();
      this.walletMethod =
        Object.keys(userInfo).length === 0
          ? WalletProviderType.WEB3AUTH_WEB3
          : WalletProviderType.WEB3AUTH_WEB2;

      this.listenToEvents(provider);

      return this.persistLastUsedWallet(walletProviderType, acc[0]);
    }

    return "";
  }

  async getAddress() {
    try {
      if (!web3AuthInitialized) {
        await this.initWeb3Auth();
      }
      if (!web3.connected || !web3.provider) {
        return "";
      }
      const provider = new ethers.providers.Web3Provider(web3.provider);
      const signer = provider.getSigner();
      return await signer.getAddress();
    } catch (err) {
      console.error("Error occurred while connecting to Web3Auth", err);
      const error = this.handleWalletError(
        MetamaskErrorCodes.NO_METAMASK_PLUGIN
      );
      return Promise.reject(error);
    }
  }

  async initWeb3Auth() {
    if (web3AuthInitialized) {
      return;
    }

    if (!web3AuthInitPromise) {
      web3AuthInitPromise = web3
        .init()
        .then(() => {
          setweb3AuthInitialized(true);
        })
        .catch((error) => {
          setweb3AuthInitialized(false);
          throw error;
        })
        .finally(() => {
          web3AuthInitPromise = null;
        });
    }

    await web3AuthInitPromise;
  }

  async listenToEvents(provider: ethers.providers.Web3Provider) {
    if (this.activeProvider) {
      // Remove existing listeners
      this.activeProvider?.removeAllListeners("accountsChanged");
      this.activeProvider?.removeAllListeners("disconnect");
    }

    // Set new provider and add listeners
    this.activeProvider = provider;

    this.activeProvider?.addListener(
      "accountsChanged",
      this.onAccountsChanged.bind(this)
    );
    this.activeProvider?.addListener(
      "disconnect",
      this.persistLastUsedWallet.bind(this)
    );
  }

  onAccountsChanged(res: string[]) {
    const walletAddress = res?.[0] ?? null;
    if (!walletAddress) {
      this.persistLastUsedWallet();
    }
  }

  persistLastUsedWallet = (
    walletProviderType?: WalletProviderType,
    walletAddress?: string
  ) => {
    if (!walletProviderType || !walletAddress) {
      clearWalletData();
      return "";
    } else {
      const walletId = getStoredWalletData().walletAddress;
      const walletType = getStoredWalletData().walletProviderType;
      if (walletId === walletAddress && walletType === walletProviderType) {
        return walletId;
      }

      storeWalletData(walletProviderType, walletAddress!);

      return walletAddress;
    }
  };

  handleWalletError(errorCode: string): string {
    let error = "";
    switch (errorCode) {
      case MetamaskErrorCodes.NO_METAMASK_PLUGIN: {
        error = "No metamask plugin found.";
        break;
      }
      case "ACTION_REJECTED": {
        error = "You rejected the transaction.";
        break;
      }
      case MetamaskErrorCodes.NO_WALLET_CONNECTED: {
        error = "You rejected the transaction.";
        break;
      }
      case MetamaskErrorCodes.WALLET_DISCONNECTED: {
        error = "Wallet has been disconnected.";
        break;
      }
      default: {
        error = "Unknown error while connecting your metamask wallet";
      }
    }
    return error;
  }

  getConnectedWallet(): WalletProviderType {
    return this.walletMethod;
  }
}
